import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, FilePenLine, X } from 'lucide-react'

import { Badge } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { UserAvatar } from '@/components/UserAvatar'
import { UserStatus } from '@/schema'
import { cn, formatFullName } from '@/utils/common'

import { ERoleType, getRolesDisplayLabel } from '../constants'
import { useGetUserById } from '../hooks'
import { useUserManagementStore } from '../store'

type PermissionGroup = {
  company_id: string
  company_name: string
  items: Array<{ id: string; label: string }>
}

const InfoRow = ({
  label,
  value,
  className,
}: {
  label: React.ReactNode
  value: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('grid grid-cols-[112px_1fr] gap-5', className)}>
      <div className="text-sm font-medium text-tertiary-600">{label}:</div>
      <div className="text-sm font-medium text-primary-900 break-words">
        {value}
      </div>
    </div>
  )
}

export const UserDetailDrawer: React.FC = () => {
  const { t } = useTranslation()
  const {
    drawerOpen,
    selectedUser,
    setDrawerOpen,
    setEditingUser,
    currentUserRole,
  } = useUserManagementStore()

  const [expandedCompanies, setExpandedCompanies] = useState<
    Record<string, boolean>
  >({})

  useEffect(() => {
    if (drawerOpen) {
      setExpandedCompanies({})
    }
  }, [drawerOpen])

  const toggleCompany = (companyId: string) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: !prev[companyId],
    }))
  }

  const { data: { data: [userDetail] = [] } = {}, isLoading } = useGetUserById(
    selectedUser?.id || ''
  )

  const permissionGroups: PermissionGroup[] = useMemo(() => {
    const roles = userDetail?.roles ?? []

    const roleWeights: Record<string, number> = {
      [ERoleType.ORGANIZATION_ADMIN]: 4,
      [ERoleType.PROJECT_LEAD]: 3,
      [ERoleType.TEAM_MEMBER]: 2
    }

    const maxWeight = Math.max(
      0,
      ...roles.map((r) => roleWeights[r.role_name as ERoleType] || 0)
    )

    const highestRoles = roles.filter((r) => {
      const weight = roleWeights[r.role_name as ERoleType] || 0
      if (maxWeight === 1 && weight === 1) return true
      return weight === maxWeight
    })

    const scopes = highestRoles
      .flatMap((r) => r.scopes ?? [])
      .filter((s) => s.company_id && s.project_id)
    if (!scopes.length) return []

    const map = new Map<string, PermissionGroup>()

    for (const s of scopes) {
      const companyId = s.company_id || 'unknown-company'
      const companyName = s.company_name || '-'

      const label = s.project_name || '-'
      const id = s.project_id || '-'

      if (!map.has(companyId)) {
        map.set(companyId, {
          company_id: companyId,
          company_name: companyName,
          items: [],
        })
      }

      const group = map.get(companyId)!
      // deduplicate check project_id
      if (!group.items.some((item) => item.id === id)) {
        group.items.push({ id, label })
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.company_name.localeCompare(b.company_name)
    )
  }, [userDetail])

  const handleClose = () => setDrawerOpen(false)

  const handleEdit = () => {
    if (!selectedUser) return
    setEditingUser(selectedUser)
    setDrawerOpen(false)
  }

  /**
   * Review documents
   */
  const reviewDocuments = useMemo(() => {
    const roles = userDetail?.roles?.filter(
      (r) => r.role_name === ERoleType.REVIEWER || r.role_name === ERoleType.VIEWER
    )
    const scopes = roles?.flatMap((r) => r.scopes ?? [])
    return scopes
      ?.filter((s) => s.company_id && s.project_id && s.deliverable_set_id)
      .map((s) => s.deliverable_set_name)
      .filter(Boolean)
  }, [userDetail?.roles])

  if (!selectedUser) return null

  const fullName = formatFullName(
    userDetail?.first_name || selectedUser.first_name,
    userDetail?.last_name || selectedUser.last_name
  )

  const displayStatus = userDetail?.status || selectedUser.status

  const getStatusLabel = (status: string | undefined) => {
    if (status === UserStatus.ACTIVE) {
      return t('userManagement.status.activeUser')
    }
    if (status === UserStatus.PENDING) {
      return t('userManagement.status.pendingUser')
    }
    return '-'
  }

  const statusLabel = getStatusLabel(displayStatus)

  const showPermission = !isLoading && permissionGroups.length > 0

  return (
    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} direction="right">
      <DrawerContent
        className="!max-w-[372px] !w-[372px] p-0 !select-text"
        data-vaul-no-drag
      >
        <DrawerHeader className="p-0 px-6 pt-2xl pb-2xl">
          <div className="flex items-start justify-between gap-6 mb-4">
            <UserAvatar user={selectedUser} size="size-16" />
            <Button
              variant="text"
              className="h-10 w-10 p-0 l-0 t-0 text-foreground-fg-quaternary-400"
              onClick={handleClose}
              aria-label={t('common.close')}
            >
              <X size={22} />
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3">
            <DrawerTitle className="text-xl font-bold text-gray-900 leading-tight break-words">
              {fullName || '-'}
            </DrawerTitle>
            {currentUserRole === ERoleType.ORGANIZATION_ADMIN && (
              <Button
                variant="text"
                className="h-10 w-10 p-0 flex-shrink-0 text-foreground-fg-quaternary-400"
                onClick={handleEdit}
                aria-label={t('common.edit')}
              >
                <FilePenLine size={20} />
              </Button>
            )}
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6">
          {/* Information */}
          <section>
            <h2 className="text-md font-semibold text-primary-900">
              {t('userManagement.drawer.information')}
            </h2>

            <div className="space-y-2">
              <InfoRow
                label={t('userManagement.drawer.firstName')}
                value={userDetail?.first_name || selectedUser.first_name || '-'}
              />
              <InfoRow
                label={t('userManagement.drawer.lastName')}
                value={userDetail?.last_name || selectedUser.last_name || '-'}
              />
              <InfoRow
                label={t('userManagement.drawer.email')}
                value={userDetail?.email || selectedUser.email || '-'}
              />
              <InfoRow
                label={t('userManagement.drawer.role')}
                value={getRolesDisplayLabel(
                  userDetail?.roles || selectedUser.roles
                )}
              />
              <InfoRow
                label={t('userManagement.drawer.status')}
                value={statusLabel}
              />
            </div>
          </section>

          {/* Loading */}
          {isLoading ? (
            <div className="mt-7 text-sm text-gray-500">
              {t('common.loading')}...
            </div>
          ) : null}

          {/* Permission */}
          {showPermission ? (
            <section className="mt-7">
              <h2 className="text-md text-primary-900">
                {t('userManagement.drawer.access')}
              </h2>

              <div className="space-y-2xl">
                {permissionGroups.map((group, idx) => (
                  <React.Fragment key={group.company_id}>
                    {idx > 0 && (
                      <div className="border-t border-gray-200 mb-xl" />
                    )}

                    <Button
                      variant="text"
                      className="p-0 h-auto hover:bg-transparent justify-start ring-0 focus-visible:ring-0 gap-0"
                      onClick={() => toggleCompany(group.company_id)}
                    >
                      <div className="w-5 mr-3 flex justify-center items-center text-foreground-fg-quaternary-400">
                        {expandedCompanies[group.company_id] ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-sm font-semibold text-tertiary-600 tracking-wide text-left">
                          {t('userManagement.drawer.company')}:{' '}
                          {group.company_name}
                        </div>
                        <Badge
                          variant="outline"
                          size="sm"
                          className="bg-gray-50 text-gray-700 border-solid border-gray-300 px-1.5"
                        >
                          {group.items.length}
                        </Badge>
                      </div>
                    </Button>

                    {expandedCompanies[group.company_id] && (
                      <ol className="list-decimal list-inside pl-8 !mt-2">
                        {group.items.map((it) => (
                          <li key={it.id} className="text-sm text-primary-900">
                            {it.label}
                          </li>
                        ))}
                      </ol>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>
          ) : null}

          {reviewDocuments?.length ? (
            <section className="mt-7">
              <h2 className="text-md text-primary-900">Review Documents</h2>

              <div className="space-y-2xl">
                <ol className="list-decimal list-inside pl-8 !mt-2">
                  {reviewDocuments.map((r) => (
                    <li key={r} className="text-sm text-primary-900">
                      {r}
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          ) : null}

        </div>
      </DrawerContent>
    </Drawer>
  )
}
